import { useTranslation } from 'react-i18next';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ELLIPSIS, paginationRange } from '@utils/paginationRange';
import { TABLE_PAGE_SIZES } from '@constants/table';

export interface PaginationBarProps {
  /** 1-based, as every caller and the API number them. */
  page: number;
  pageSize: number;
  pageCount: number;
  /** Rows across every page, not just the visible one. */
  rowCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizes?: readonly number[];
}

/**
 * Whether the bar has anything to show. Exported because a caller drawing chrome
 * around it — a bordered strip, say — has to make the same call, or it draws an
 * empty band under an empty table.
 *
 * Rows are the only condition. Hiding the bar when everything fit on one page
 * also hid the size selector, so choosing a size large enough to fit everything
 * removed the control that had just been used and left no way back to a smaller
 * one. The page links are what depend on there being more than one page, and
 * they hide on their own below.
 */
export const showsPaginationBar = ({
  rowCount,
}: Pick<PaginationBarProps, 'rowCount'>): boolean => rowCount > 0;

/**
 * The pagination bar itself, driven by plain numbers so it serves both the
 * TanStack-backed DataTable and the hand-rolled tables that only need to slice
 * an array. DataTablePagination is the adapter for the former.
 */
export function PaginationBar({
  page,
  pageSize,
  pageCount,
  rowCount,
  onPageChange,
  onPageSizeChange,
  pageSizes = TABLE_PAGE_SIZES,
}: PaginationBarProps) {
  const { t } = useTranslation();

  if (!showsPaginationBar({ rowCount })) return null;

  const firstRow = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(rowCount, page * pageSize);

  return (
    <div className="flex flex-col items-center justify-between gap-3 @lg/table:flex-row">
      <div className="flex items-center gap-3">
        <p className="revy-figure whitespace-nowrap text-xs text-muted-foreground">
          {t('table.showingRange', { from: firstRow, to: lastRow, total: rowCount })}
        </p>

        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger aria-label={t('table.rowsPerPage')} className="h-8 w-[4.5rem] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="revy-ui">
            {pageSizes.map((size) => (
              <SelectItem key={size} value={String(size)} className="revy-figure text-xs">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Page links only: with a single page there is nowhere to go, but the
          range and the size selector above still say something useful. */}
      {pageCount > 1 && (
      <Pagination className="mx-0 w-auto justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              aria-label={t('table.previousPage')}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            />
          </PaginationItem>

          {paginationRange(page, pageCount).map((slot, index) =>
            slot === ELLIPSIS ? (
              <PaginationItem key={`gap-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={slot}>
                <PaginationLink
                  size="icon-sm"
                  isActive={slot === page}
                  aria-label={t('table.goToPage', { page: slot })}
                  onClick={() => onPageChange(slot as number)}
                  className="revy-figure"
                >
                  {slot}
                </PaginationLink>
              </PaginationItem>
            ),
          )}

          <PaginationItem>
            <PaginationNext
              aria-label={t('table.nextPage')}
              disabled={page >= pageCount}
              onClick={() => onPageChange(page + 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      )}
    </div>
  );
}
